import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import FormRenderer from '../forms/FormRenderer';
import formService from '../../services/formService';
import ratingService from '../../services/ratingService';
import applicationService from '../../services/applicationService';
import { supabase } from '../../utils/supabase';

const ApplicationDetailsModal = ({ 
  isOpen, 
  onClose, 
  application 
}) => {
  const [formResponseData, setFormResponseData] = useState(null);
  const [loadingFormResponses, setLoadingFormResponses] = useState(false);
  const [applicantRating, setApplicantRating] = useState(null);
  const [applicantReviews, setApplicantReviews] = useState(0);
  const [loadingRating, setLoadingRating] = useState(false);
  const [realMatchBreakdown, setRealMatchBreakdown] = useState(null);
  const [loadingMatchData, setLoadingMatchData] = useState(false);
  const [proposedRate, setProposedRate] = useState(null);
  const [proposedRateMin, setProposedRateMin] = useState(null);
  const [proposedRateMax, setProposedRateMax] = useState(null);
  const [availabilityStart, setAvailabilityStart] = useState(null);
  const [portfolioLinks, setPortfolioLinks] = useState([]);

  // Load form responses and real rating data when modal opens
  useEffect(() => {
    const loadData = async () => {
      if (!isOpen || !application?.id) return;
      
      // Load form responses
      setLoadingFormResponses(true);
      try {
        const result = await formService.getApplicationFormResponses(application.id);
        if (result.success && result.data && result.data.length > 0) {
          setFormResponseData(result.data[0]);
        } else {
          setFormResponseData(null);
        }
      } catch (error) {
        console.error('Error loading form responses:', error);
        setFormResponseData(null);
      } finally {
        setLoadingFormResponses(false);
      }

      // Load real rating data from applicant profile
      if (application?.applicant_id) {
        setLoadingRating(true);
        try {
          const result = await ratingService.getApplicantRating(application.applicant_id);
          if (result.success && result.data) {
            setApplicantRating(result.data.rating);
            setApplicantReviews(result.data.totalReviews);
          } else {
            console.error('Failed to fetch applicant rating:', result.error);
            setApplicantRating(0);
            setApplicantReviews(0);
          }
        } catch (error) {
          console.error('Error loading rating data:', error);
          setApplicantRating(0);
          setApplicantReviews(0);
        } finally {
          setLoadingRating(false);
        }
      }

      // Load real match breakdown data and application details
      setLoadingMatchData(true);
      try {
        const result = await applicationService.getApplicationDetails(application.id);
        if (result.success && result.data) {
          if (result.data.matchBreakdown) {
            setRealMatchBreakdown(result.data.matchBreakdown);
          }
          if (result.data.proposedRate) {
            setProposedRate(result.data.proposedRate);
          }
          if (result.data.proposedRateMin) {
            setProposedRateMin(result.data.proposedRateMin);
          }
          if (result.data.proposedRateMax) {
            setProposedRateMax(result.data.proposedRateMax);
          }
          if (result.data.availabilityStart) {
            setAvailabilityStart(result.data.availabilityStart);
          }
          if (result.data.portfolioLinks && result.data.portfolioLinks.length > 0) {
            setPortfolioLinks(result.data.portfolioLinks);
          }
        } else {
          console.error('Failed to fetch application details:', result.error);
        }
      } catch (error) {
        console.error('Error loading application data:', error);
      } finally {
        setLoadingMatchData(false);
      }
    };

    loadData();
  }, [isOpen, application?.id, application?.applicant_id]);

  if (!isOpen || !application) return null;

  // Get match breakdown - use real data if available
  const getMatchBreakdown = () => {
    // Use fetched real data first
    if (realMatchBreakdown && typeof realMatchBreakdown === 'object') {
      return realMatchBreakdown;
    }
    
    // Fallback to application prop if available
    if (application.match_breakdown && typeof application.match_breakdown === 'object') {
      return application.match_breakdown;
    }
    
    // Last resort: return null to show "not calculated" message
    return null;
  };

  const matchBreakdown = getMatchBreakdown();

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${Number(amount).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-accent-50">
          <div className="flex items-center space-x-4">
            <img
              src={application.avatar || '/assets/images/Logo/webp/VAS-LOGO.webp'}
              alt={application.name || 'Candidate'}
              className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-sm"
              onError={(e) => {
                e.target.src = '/assets/images/Logo/webp/VAS-LOGO.webp';
              }}
            />
            <div>
              <h2 className="text-2xl font-bold text-text-primary">
                {application.name || 'Unknown Candidate'}
              </h2>
              <p className="text-text-secondary">
                {application.title || 'Virtual Assistant'}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                  application.status === 'pending' ? 'bg-warning-100 text-warning' :
                  application.status === 'shortlisted' ? 'bg-primary-100 text-primary' :
                  application.status === 'interviewed' ? 'bg-accent-100 text-accent' :
                  application.status === 'accepted' ? 'bg-success-100 text-success' :
                  application.status === 'rejected' ? 'bg-error-100 text-error' :
                  'bg-secondary-100 text-text-secondary'
                }`}>
                  {application.status || 'pending'}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-8">
            {/* Application Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={20} className="text-blue-600" />
                  <span className="font-medium text-blue-900">Rating</span>
                </div>
                <div className="mt-1">
                  {loadingRating ? (
                    <div className="animate-pulse h-8 bg-blue-200 rounded"></div>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-blue-800">
                        {applicantRating !== null ? Number(applicantRating).toFixed(1) : 'N/A'}
                      </p>
                      {applicantReviews > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Based on {applicantReviews} review{applicantReviews !== 1 ? 's' : ''}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Target" size={20} className="text-green-600" />
                  <span className="font-medium text-green-900">Match Score</span>
                </div>
                <p className="text-lg font-bold text-green-800 mt-1">
                  {application.matchPercentage || 0}%
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Briefcase" size={20} className="text-purple-600" />
                  <span className="font-medium text-purple-900">Experience</span>
                </div>
                <p className="text-lg font-bold text-purple-800 mt-1">
                  {application.experience || 0} years
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="DollarSign" size={20} className="text-orange-600" />
                  <span className="font-medium text-orange-900">Rate</span>
                </div>
                <p className="text-lg font-bold text-orange-800 mt-1">
                  {proposedRateMin && proposedRateMax 
                    ? `${formatCurrency(proposedRateMin)} - ${formatCurrency(proposedRateMax)}/hr`
                    : `${formatCurrency(proposedRate || application.proposedRate)}/hr`
                  }
                </p>
              </div>
            </div>

            {/* Cover Letter */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="FileText" size={20} className="mr-2 text-primary" />
                Cover Letter
              </h3>
              <div className="prose prose-sm max-w-none">
                {application.coverLetter ? (
                  <p className="text-text-secondary whitespace-pre-wrap leading-relaxed">
                    {application.coverLetter}
                  </p>
                ) : (
                  <p className="text-text-muted italic">
                    No cover letter provided with this application.
                  </p>
                )}
              </div>
            </div>

            {/* Form Responses */}
            {loadingFormResponses && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-3 text-text-secondary">Loading form responses...</span>
              </div>
            )}

            {!loadingFormResponses && formResponseData && (
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="FileText" size={20} className="mr-2 text-blue-600" />
                  Application Form Responses
                </h3>
                <div className="space-y-4">
                  {Object.entries(formResponseData.responses || {}).map(([questionId, response]) => (
                    <div key={questionId} className="bg-white p-4 rounded-lg border border-blue-100">
                      <div className="font-medium text-sm text-text-primary mb-2">
                        {response.question}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {Array.isArray(response.answer) ? (
                          <ul className="list-disc list-inside space-y-1">
                            {response.answer.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : response.type === 'url' ? (
                          <a 
                            href={response.answer} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary-dark underline flex items-center"
                          >
                            {response.answer}
                            <Icon name="ExternalLink" size={14} className="ml-1" />
                          </a>
                        ) : (
                          <p className="whitespace-pre-wrap">{response.answer}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="text-xs text-text-muted italic mt-4">
                    Submitted: {formatDate(formResponseData.submitted_at)}
                  </div>
                </div>
              </div>
            )}

            {/* Portfolio Links */}
            {portfolioLinks && portfolioLinks.length > 0 && (
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Link" size={20} className="mr-2 text-indigo-600" />
                  Portfolio Links
                </h3>
                <div className="space-y-2">
                  {portfolioLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-primary hover:text-primary-dark text-sm group"
                    >
                      <Icon name="ExternalLink" size={14} className="mr-2" />
                      <span className="group-hover:underline break-all">{link}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="Info" size={20} className="mr-2 text-primary" />
                  Application Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Applied Date:</span>
                    <span className="font-medium">{formatDate(application.appliedAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Job Position:</span>
                    <span className="font-medium">{application.jobTitle || 'Unknown Job'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Location:</span>
                    <span className="font-medium">{application.location || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Proposed Rate:</span>
                    <span className="font-medium">
                      {proposedRateMin && proposedRateMax 
                        ? `${formatCurrency(proposedRateMin)} - ${formatCurrency(proposedRateMax)}/hr`
                        : `${formatCurrency(proposedRate || application.proposedRate)}/hr`
                      }
                    </span>
                  </div>
                  {availabilityStart && (
                    <div className="flex justify-between">
                      <span className="text-text-muted">Available From:</span>
                      <span className="font-medium">{new Date(availabilityStart).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                  {application.interviewScheduledAt && (
                    <div className="flex justify-between">
                      <span className="text-text-muted">Interview Scheduled:</span>
                      <span className="font-medium">{formatDate(application.interviewScheduledAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Match Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
                  <Icon name="BarChart3" size={20} className="mr-2 text-primary" />
                  Match Breakdown
                </h3>
                {loadingMatchData ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                    <span className="ml-2 text-text-secondary text-sm">Loading...</span>
                  </div>
                ) : matchBreakdown ? (
                  <div className="space-y-4 text-sm">
                    {/* Skills Score */}
                    {matchBreakdown.skillsScore !== undefined && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-text-secondary font-medium">Skills Match</span>
                          <span className="font-bold text-text-primary">{Math.round(matchBreakdown.skillsScore)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              matchBreakdown.skillsScore >= 80 ? 'bg-green-500' :
                              matchBreakdown.skillsScore >= 60 ? 'bg-blue-500' :
                              matchBreakdown.skillsScore >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(matchBreakdown.skillsScore || 0, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-text-muted mt-1">Weight: 50%</div>
                      </div>
                    )}

                    {/* Experience Score */}
                    {matchBreakdown.experienceScore !== undefined && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-text-secondary font-medium">Experience Match</span>
                          <span className="font-bold text-text-primary">{Math.round(matchBreakdown.experienceScore)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              matchBreakdown.experienceScore >= 80 ? 'bg-green-500' :
                              matchBreakdown.experienceScore >= 60 ? 'bg-blue-500' :
                              matchBreakdown.experienceScore >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(matchBreakdown.experienceScore || 0, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-text-muted mt-1">Weight: 30%</div>
                      </div>
                    )}

                    {/* Rate Score */}
                    {matchBreakdown.rateScore !== undefined && (
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-text-secondary font-medium">Rate Compatibility</span>
                          <span className="font-bold text-text-primary">{Math.round(matchBreakdown.rateScore)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              matchBreakdown.rateScore >= 80 ? 'bg-green-500' :
                              matchBreakdown.rateScore >= 60 ? 'bg-blue-500' :
                              matchBreakdown.rateScore >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(matchBreakdown.rateScore || 0, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-text-muted mt-1">Weight: 20%</div>
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-gray-300">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-text-primary">Overall Match Score:</span>
                        <span className="text-xl font-bold text-green-700">{application.matchPercentage || 0}%</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-text-muted italic">
                    Match breakdown not calculated for this application.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsModal;
